/**
 * FIX-COLUMNCONFIG-TYPE-DISCIPLINE-1
 *
 * App-facing shape is nested. Persistence stays flat JSONB:
 *   { labels: true, defaultView: 'board', visibleTabs: ['overview', 'board'] }
 *
 * Normalize on read / toFlat on write. Writers merge by section so column
 * toggles never clobber defaultView/visibleTabs (and vice versa).
 */

export type ColumnConfig = {
  columns: Record<string, boolean>;
  defaultView?: string;
  visibleTabs?: string[];
};

/** Flat JSONB wire / storage form (not the TS app model). */
export type ColumnConfigFlat = Record<string, boolean | string | string[]>;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function readDefaultView(o: Record<string, unknown>): string | undefined {
  const dv = o.defaultView ?? o.default_view;
  return typeof dv === 'string' && dv.trim() ? dv.trim() : undefined;
}

function readVisibleTabs(o: Record<string, unknown>): string[] | undefined {
  const vt = o.visibleTabs ?? o.visible_tabs;
  if (!Array.isArray(vt)) return undefined;
  const filtered = vt.filter((t): t is string => typeof t === 'string');
  return filtered;
}

function readBooleanMap(o: Record<string, unknown>): Record<string, boolean> {
  const columns: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(o)) {
    if (typeof v === 'boolean') columns[k] = v;
  }
  return columns;
}

/** Flat storage or already-nested → nested app model. */
export function normalizeColumnConfig(raw: unknown): ColumnConfig {
  if (!isPlainObject(raw)) return { columns: {} };

  // Nested write-through (tests / future callers)
  if (isPlainObject(raw.columns)) {
    return {
      columns: readBooleanMap(raw.columns),
      ...(readDefaultView(raw) ? { defaultView: readDefaultView(raw) } : {}),
      ...(readVisibleTabs(raw) ? { visibleTabs: readVisibleTabs(raw) } : {}),
    };
  }

  // Flat storage
  const columns: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (
      k === 'defaultView' ||
      k === 'default_view' ||
      k === 'visibleTabs' ||
      k === 'visible_tabs' ||
      k === 'columns'
    ) {
      continue;
    }
    if (typeof v === 'boolean') columns[k] = v;
  }

  const defaultView = readDefaultView(raw);
  const visibleTabs = readVisibleTabs(raw);
  return {
    columns,
    ...(defaultView ? { defaultView } : {}),
    ...(visibleTabs ? { visibleTabs } : {}),
  };
}

/** Nested app model → flat JSONB for API/DB. */
export function toFlatColumnConfig(cfg: ColumnConfig): ColumnConfigFlat {
  const out: ColumnConfigFlat = { ...cfg.columns };
  if (cfg.defaultView) out.defaultView = cfg.defaultView;
  if (cfg.visibleTabs) out.visibleTabs = [...cfg.visibleTabs];
  return out;
}

/** Replace/merge column booleans only — preserve view routing. */
export function withColumnToggles(
  current: ColumnConfig,
  columnsPatch: Record<string, boolean>,
): ColumnConfig {
  return {
    ...current,
    columns: { ...current.columns, ...columnsPatch },
  };
}

/** Replace visibleTabs only — preserve columns + defaultView. */
export function withVisibleTabs(
  current: ColumnConfig,
  visibleTabs: string[],
): ColumnConfig {
  return { ...current, visibleTabs: [...visibleTabs] };
}

/**
 * Merge a flat patch into existing flat storage without clobbering sibling
 * sections when the patch omits them (section-aware).
 */
export function mergeColumnConfigFlat(
  existing: unknown,
  patch: unknown,
): ColumnConfigFlat {
  const cur = normalizeColumnConfig(existing);
  if (!isPlainObject(patch)) return toFlatColumnConfig(cur);

  const hasDefaultView = 'defaultView' in patch || 'default_view' in patch;
  const hasVisibleTabs = 'visibleTabs' in patch || 'visible_tabs' in patch;
  const hasNestedColumns = isPlainObject(patch.columns);

  const inc = normalizeColumnConfig(patch);

  // Flat patch: boolean keys are column toggles even when view keys also present.
  const columnPatch = hasNestedColumns
    ? inc.columns
    : readBooleanMap(
        Object.fromEntries(
          Object.entries(patch).filter(
            ([k]) =>
              k !== 'defaultView' &&
              k !== 'default_view' &&
              k !== 'visibleTabs' &&
              k !== 'visible_tabs' &&
              k !== 'columns',
          ),
        ) as Record<string, unknown>,
      );

  const next: ColumnConfig = {
    columns: { ...cur.columns, ...columnPatch },
    defaultView: hasDefaultView ? inc.defaultView : cur.defaultView,
    visibleTabs: hasVisibleTabs ? inc.visibleTabs : cur.visibleTabs,
  };

  // Drop undefined optional fields from explicit clear via empty omit:
  if (hasDefaultView && inc.defaultView === undefined) delete next.defaultView;
  if (hasVisibleTabs && inc.visibleTabs === undefined) delete next.visibleTabs;

  return toFlatColumnConfig(next);
}
